import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import type { Lens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { useRealmAuthContext } from '@interxfi/api-client/context';
import { buildIxmUserauthV1ProgramQueryServiceProgramListKey } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-program-query-service-program-list';
import { useIxmUserauthV1ProgramTenantServiceCreateProgramTenant } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-program-tenant-service-create-program-tenant';
import { buildIxmUserauthV1SessionServiceWhoamiKey } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-session-service-whoami';
import type { OrganizationMembership } from '@interxfi/api-client/realm-auth/store';
import { TenantContext } from '@interxfi/api-client/realm-context';
import { getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { useMergedDialogOpenProps } from '@interxfi/design-system/components/dialog/utils';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { StatusBar } from '@interxfi/design-system/components/status-bar/status-bar';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import AIIcon from '@interxfi/design-system/icons/react/icon-ai';
import UndoIcon from '@interxfi/design-system/icons/react/icon-undo';
import type { IxmUserauthV1JoinedRealmAccess, IxmUserauthV1ProgramTenantServiceCreateProgramTenantRequest } from '@interxfi/types/generated/api';
import {
  getOrganizationLabel,
  UserauthOrganizationComboboxWithLens,
} from '../../../components/data-combobox-input/entity-config/userauth-organization';
import { AppComboboxInputWithLens } from '../../../components/form/app-combobox-input';
import { FormWizard, type FormWizardDrawerProps } from '../../../components/form/drawer/wizard/form-wizard';
import { useFormWizardContextValue } from '../../../components/form/drawer/wizard/form-wizard-context';
import type { FormWizardReviewStep } from '../../../components/form/drawer/wizard/step/form-wizard-review-step';
import { useFormWizardSeedContext } from '../../../components/form/drawer/wizard/step/form-wizard-seed-context';
import type { StepArray } from '../../../components/form/drawer/wizard/step/form-wizard-step-builder';
import { getFixedOrMutableIdSchema } from '../../../components/form/schema';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../util/error-handler';
import {
  defaultProgramContactsFormValues,
  formValuesToProgramContacts,
  mapProgramContactsToFormConfirmationGroups,
  ProgramContactsForm,
  type ProgramContactsFormValues,
  programContactsFormSchema,
} from './form/contacts/program-contacts-form';
import {
  defaultProgramControlsFormValues,
  formValuesToProgramControls,
  mapProgramControlsToFormConfirmationGroup,
  ProgramControlsForm,
  type ProgramControlsFormValues,
  programControlsFormSchema,
} from './form/controls/program-controls-form';
import {
  defaultProgramClearingDetailsFormValues,
  formValuesToClearingDetails,
  ProgramClearingDetailsForm,
  type ProgramClearingDetailsFormValues,
  programClearingDetailsFormSchema,
} from './form/program-clearing-details-form';
import {
  defaultProgramDetailsFormValues,
  formValuesToProgramDetails,
  mapProgramDetailsToFormConfirmationGroups,
  onSubmitProgramDetailsFormSchema,
  ProgramDetailsForm,
  programDetailsFormSchema,
} from './form/program-details-form';
import { generateSeedProgramData } from './form/seed-program-data';
import { getProgramDetailPath } from './program-routing';

const createProgramDetailsStepSchema = z.object({
  realmId: getFixedOrMutableIdSchema(),
  organizationId: getFixedOrMutableIdSchema('Organization is required'),
  details: programDetailsFormSchema,
});

const onSubmitProgramDetailsStepSchema = createProgramDetailsStepSchema.safeExtend({
  details: onSubmitProgramDetailsFormSchema,
});

type CreateProgramDetailsStepValues = z.infer<typeof createProgramDetailsStepSchema>;

export type CreateProgramFormSteps = {
  details: CreateProgramDetailsStepValues;
  controls: ProgramControlsFormValues;
  contacts: ProgramContactsFormValues;
  clearingDetails: ProgramClearingDetailsFormValues;
};

interface FormContext {
  access: IxmUserauthV1JoinedRealmAccess | undefined;
  setAccess: Dispatch<SetStateAction<IxmUserauthV1JoinedRealmAccess | undefined>>;
}

function findAcceptableAccess(
  organizationId: string | undefined,
  accesses: IxmUserauthV1JoinedRealmAccess[] | undefined,
  organizationMemberships: OrganizationMembership[] | undefined,
) {
  let platformAccess: IxmUserauthV1JoinedRealmAccess | undefined;
  let organizationAccess: IxmUserauthV1JoinedRealmAccess | undefined;

  for (const access of accesses || []) {
    if (!platformAccess && access.tenant?.tenantType === TenantContext.Platform) {
      platformAccess = access;
    }

    const matchingOrganizationMembership = organizationMemberships?.find((membership) => membership.organization?.organizationId === organizationId);

    if (
      access.tenant?.tenantType === TenantContext.Organization &&
      organizationId === access.tenant.tenantId &&
      matchingOrganizationMembership?.organizationMember?.data?.permissions?.isAdmin
    ) {
      organizationAccess = access;
    }
  }

  return platformAccess || organizationAccess;
}

function useAcceptableAccess(organizationId: string | undefined) {
  const authContext = useRealmAuthContext();

  return findAcceptableAccess(organizationId, authContext?.accesses, authContext?.organizationMemberships);
}

function DetailStepForm({ lens }: { lens: Lens<CreateProgramDetailsStepValues> }) {
  const { accesses, organizationMemberships } = useRealmAuthContext() || {};
  const ctx = useFormWizardContextValue<CreateProgramFormSteps, FormContext>();
  const setAccess = ctx?.setAccess;
  const organizationId = useWatch(lens.focus('organizationId').interop());
  const realmId = useWatch(lens.focus('realmId').interop());
  const selectedOrganizationAccess = useAcceptableAccess(organizationId?.value);
  const hasPlatformAccess = Boolean(accesses?.some((access) => access.tenant?.tenantType === TenantContext.Platform));
  const selectedRealmIsSimulated =
    accesses?.find((access) => access.realm?.realmId === realmId?.value)?.realm?.data?.spec?.metadata?.connectivity === 'simulated';

  useEffect(() => {
    setAccess?.((prev) => {
      if (prev?.access?.realmAccessId === selectedOrganizationAccess?.access?.realmAccessId) {
        return prev;
      }

      return selectedOrganizationAccess;
    });
  }, [setAccess, selectedOrganizationAccess]);

  const { hasSeedData, seedData, undoSeed } = useFormWizardSeedContext<CreateProgramFormSteps>();
  const handleSeedData = useCallback(async () => {
    const { controls, details, clearingDetails, contacts } = await generateSeedProgramData();

    seedData({
      details: {
        ...details,
        details: details.details,
        organizationId,
        realmId,
      },
      controls,
      clearingDetails,
      contacts,
    });
  }, [seedData, organizationId, realmId]);

  const realmOptions = useMemo(
    () =>
      (accesses || []).reduce<InputDropdownOption[]>((acc, curr) => {
        if (
          curr.realm?.data?.spec?.tenantTypes?.some((t) => t.name === TenantContext.Program) &&
          !acc?.find((v) => v?.value === curr.realm?.realmId) &&
          (curr.realm.data.spec.metadata?.connectivity === 'simulated' || hasPlatformAccess)
        ) {
          acc.push({
            label: curr.realm.data.spec.name,
            value: curr.realm.realmId,
          });
        }

        return acc;
      }, []),
    [accesses, hasPlatformAccess],
  );

  const organizationLevelUserOptions = useMemo(() => {
    return (organizationMemberships || []).reduce<InputDropdownOption[]>((acc, membership) => {
      if (
        membership.organization?.organizationId &&
        !acc.some((opt) => opt.value === membership.organization?.organizationId) &&
        membership.organizationMember?.data?.permissions?.isAdmin
      ) {
        acc.push({
          value: membership.organization.organizationId,
          label: getOrganizationLabel(membership.organization),
        });
      }

      return acc;
    }, []);
  }, [organizationMemberships]);

  return (
    <VerticalStack gap={4}>
      {selectedRealmIsSimulated ? (
        <StatusBar
          actions={
            hasSeedData
              ? [
                  {
                    id: 'undo',
                    triggerStyle: 'button',
                    ctaText: 'Undo',
                    ctaIcon: <UndoIcon aria-hidden />,
                    type: 'button',
                    button: {
                      onClick: undoSeed,
                    },
                  },
                ]
              : [
                  {
                    id: 'populate',
                    triggerStyle: 'button',
                    ctaText: 'Populate',
                    type: 'button',
                    button: {
                      onClick: handleSeedData,
                    },
                  },
                ]
          }
          icon={<AIIcon aria-hidden width={16} />}
          title="Populate this form with realistic data?"
          variant="primary"
        />
      ) : null}

      <VerticalStack gap={4}>
        {realmId?.kind === 'mutable' ? (
          <AppComboboxInputWithLens label="Realm" lens={lens.focus('realmId.value')} options={realmOptions} required />
        ) : null}

        {organizationId?.kind === 'mutable' ? (
          hasPlatformAccess ? (
            <UserauthOrganizationComboboxWithLens
              label="Organization"
              lens={lens.focus('organizationId.value')}
              required
              withAccess={selectedOrganizationAccess}
            />
          ) : (
            <AppComboboxInputWithLens
              label="Organization"
              lens={lens.focus('organizationId.value')}
              options={organizationLevelUserOptions}
              required
            />
          )
        ) : null}

        <ProgramDetailsForm lens={lens.focus('details')} showTooltips />
      </VerticalStack>
    </VerticalStack>
  );
}

function getInitialValues(organizationId: string | undefined, realmId: string | undefined): CreateProgramFormSteps {
  return {
    details: {
      realmId: {
        kind: realmId ? 'fixed' : 'mutable',
        value: realmId ?? '',
      },
      organizationId: {
        kind: organizationId ? 'fixed' : 'mutable',
        value: organizationId ?? '',
      },
      details: defaultProgramDetailsFormValues,
    },
    controls: defaultProgramControlsFormValues,
    contacts: defaultProgramContactsFormValues,
    clearingDetails: defaultProgramClearingDetailsFormValues,
  };
}

function handleMapFormData(data: CreateProgramFormSteps): IxmUserauthV1ProgramTenantServiceCreateProgramTenantRequest {
  return {
    realmId: data.details.realmId.value,
    organizationId: data.details.organizationId.value,
    programCode: data.clearingDetails.programCode.value,
    programSpec: {
      details: {
        ...formValuesToProgramDetails(data.details.details),
        contacts: formValuesToProgramContacts(data.contacts),
      },
      controls: formValuesToProgramControls(data.controls),
      clearingDetails: formValuesToClearingDetails(data.clearingDetails),
    },
    tenantSpec: {
      name: data.details.details.programInformation.name,
    },
  };
}

const reviewStep: FormWizardReviewStep<IxmUserauthV1ProgramTenantServiceCreateProgramTenantRequest, FormContext> = {
  kind: 'review',
  saveCta: 'Create Program',
  buildConfirmationDetails: (request) => {
    return [
      ...mapProgramDetailsToFormConfirmationGroups(request.programSpec.details),
      mapProgramControlsToFormConfirmationGroup(request.programSpec.controls, request.programId, true),
      ...mapProgramContactsToFormConfirmationGroups(request.programSpec.details.contacts),
      {
        id: 'clearingDetails',
        title: 'Program Codes & IDs',
        list: {
          items: [
            getReadonlyListItemString('programCode', 'Program Code', request.programCode),
            getReadonlyListItemString('shortName', 'Short Name', request.programSpec.clearingDetails.shortName),
            getReadonlyListItemString('mpid', 'MPID', request.programSpec.clearingDetails.mpid),
            getReadonlyListItemString('taxVendorId', 'Tax Vendor ID', request.programSpec.clearingDetails.taxVendorId),
            getReadonlyListItemString('proxyVendorId', 'Proxy Vendor ID', request.programSpec.clearingDetails.proxyVendorId),
          ],
        },
      },
    ];
  },
};

function ControlsForm({ lens }: { lens: Lens<ProgramControlsFormValues> }) {
  return (
    <VerticalStack gap={4}>
      <Typography.Level3>Controls</Typography.Level3>

      <ProgramControlsForm lens={lens} programId={undefined} />
    </VerticalStack>
  );
}

function ContactsForm({ lens }: { lens: Lens<ProgramContactsFormValues> }) {
  return (
    <VerticalStack>
      <ProgramContactsForm lens={lens} />
    </VerticalStack>
  );
}

function ClearingDetailsForm({ lens }: { lens: Lens<ProgramClearingDetailsFormValues> }) {
  return (
    <VerticalStack gap={4}>
      <Typography.Level3>Codes &amp; IDs</Typography.Level3>

      <ProgramClearingDetailsForm lens={lens} />
    </VerticalStack>
  );
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to create program', 'create a program');

const detailsStep = {
  id: 'details',
  kind: 'form',
  schema: zodResolver(createProgramDetailsStepSchema),
  submitSchema: onSubmitProgramDetailsStepSchema,
  children: DetailStepForm,
} as const;

const controlsStep = {
  id: 'controls',
  kind: 'form',
  schema: zodResolver(programControlsFormSchema),
  children: ControlsForm,
} as const;

const contactsStep = {
  id: 'contacts',
  kind: 'form',
  schema: zodResolver(programContactsFormSchema),
  children: ContactsForm,
} as const;

const clearingDetailsStep = {
  id: 'clearingDetails',
  kind: 'form',
  schema: zodResolver(programClearingDetailsFormSchema),
  children: ClearingDetailsForm,
} as const;

const initialSteps: StepArray<CreateProgramFormSteps, FormContext> = [detailsStep, controlsStep, contactsStep, clearingDetailsStep] as const;

interface CreateProgramDrawerProps extends FormWizardDrawerProps {
  realmId?: string;
  organizationId?: string;
}

export function CreateProgramDrawer({
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  realmId,
  organizationId,
  ...rest
}: CreateProgramDrawerProps) {
  const queryClient = useQueryClient();
  const initialAccess = useAcceptableAccess(organizationId);
  const [accessContext, setAccessContext] = useState<IxmUserauthV1JoinedRealmAccess | undefined>(initialAccess);
  const initialContext = useMemo(() => ({ access: accessContext, setAccess: setAccessContext }), [accessContext]);
  const addToast = useAddToast();
  const navigate = useNavigate();
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({
    isOpen: providedIsOpen,
    onOpenChange: providedOnOpenChange,
  });
  const {
    mutateAsync,
    error,
    isPending,
    reset: resetMutation,
  } = useIxmUserauthV1ProgramTenantServiceCreateProgramTenant(
    {
      onSuccess: async () => {
        try {
          // TODO: maybe invalidate whoami on an interval until a realm access is found for the new program, for a set number of tries (eventual consistency)
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: buildIxmUserauthV1ProgramQueryServiceProgramListKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: buildIxmUserauthV1SessionServiceWhoamiKey(),
            }),
          ]);
        } catch {}
      },
    },
    accessContext,
  );
  useErrorHandler(error, errorMessageGetter);
  const [buttonState, buttonRef] = useButtonState(isPending, error);
  const initialData = useMemo(() => getInitialValues(organizationId, realmId), [organizationId, realmId]);

  const handleSave = useCallback(
    async (request: IxmUserauthV1ProgramTenantServiceCreateProgramTenantRequest) => {
      const response = await mutateAsync(request);

      if (response?.programId) {
        addToast({
          id: 'programCreated',
          content: 'Program created',
          variant: 'success',
        });

        const navigateTo = getProgramDetailPath(response.programId);
        if (navigateTo) {
          await navigate({ to: navigateTo });
        }
      }
    },
    [addToast, mutateAsync, navigate],
  );

  const handleReset = useCallback(() => {
    resetMutation();
  }, [resetMutation]);

  return (
    <FormWizard<CreateProgramFormSteps, IxmUserauthV1ProgramTenantServiceCreateProgramTenantRequest, FormContext>
      description="Create a new Program"
      handleMapFormData={handleMapFormData}
      initialContext={initialContext}
      initialData={initialData}
      initialSteps={initialSteps}
      isOpen={isOpen}
      onComplete={handleSave}
      onOpenChange={onOpenChange}
      onReset={handleReset}
      reviewStep={reviewStep}
      saveCtaRef={buttonRef}
      saveCtaState={buttonState}
      title="New Program"
      {...rest}
    />
  );
}
